import './tokens-extra.css';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';

/**
 * Score numbers in fixed-width digit boxes (0.62em each), so 9 to 10 never shifts the pad or the
 * board. Empty leading slots stay blank, not zero. A changed digit pops in from below.
 *
 * Sizes: 'sm' for lists, 'pad' for the keeper pad, 'board' for the landscape courtside board,
 * where the digits scale with the shorter side of the screen (vmin), so the same component fills
 * a phone propped sideways on a bag.
 *
 *   <ScoreDigits value={score.a} team="a" size="pad" label="Home" />
 *   <ScoreDigits value={21} size="board" slots={2} />
 */
export type ScoreDigitsSize = 'sm' | 'pad' | 'board';

const SIZE: Record<ScoreDigitsSize, string> = {
  sm: 'text-3xl',
  pad: 'text-[5.5rem]',
  board: 'text-[min(58vmin,26vw)]',
};

const FILL = { a: 'bg-team-a', b: 'bg-team-b' } as const;

export interface ScoreDigitsProps {
  value: number;
  /** Minimum number of digit slots. Default 2. */
  slots?: number;
  size?: ScoreDigitsSize;
  /** Team fill behind the digits (A peach, B sky). None: transparent. */
  team?: 'a' | 'b' | null;
  /** Spoken before the number, "Home 12". */
  label?: string;
  className?: string;
}

/** Digits right-aligned in at least `slots` boxes; leading slots are ''. Negative and NaN become 0. */
export function splitDigits(value: number, slots = 2): string[] {
  const v = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  const digits = [...String(v)];
  const pad = Math.max(0, slots - digits.length);
  return [...Array.from({ length: pad }, () => ''), ...digits];
}

export function ScoreDigits({ value, slots = 2, size = 'pad', team = null, label, className }: ScoreDigitsProps) {
  const digits = splitDigits(value, slots);
  // The digits of the last commit (updated in an effect, so StrictMode double renders agree).
  const committed = useRef<string[]>(digits);
  const before = committed.current;
  useEffect(() => {
    committed.current = digits;
  });
  const spoken = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  return (
    <span
      role="img"
      aria-label={label ? `${label} ${spoken}` : String(spoken)}
      className={clsx(
        'inline-flex items-center justify-center font-display font-black italic leading-[0.85] text-ink',
        SIZE[size],
        team && [
          FILL[team],
          'rounded-tts border-(length:--tts-border-heavy) border-ink px-[0.08em] py-[0.06em] shadow-hard',
        ],
        className,
      )}
    >
      {digits.map((d, i) => {
        // Position from the right, so a new tens digit does not re-key the ones digit.
        const pos = digits.length - i;
        const changed = before.length === digits.length ? before[i] !== d : true;
        return (
          <span
            key={`${pos}-${d}`}
            aria-hidden="true"
            className="tts-ui-digit"
            data-anim={changed && d !== '' ? '' : undefined}
          >
            {d === '' ? ' ' : d}
          </span>
        );
      })}
    </span>
  );
}
